import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, coverUrl, uploadFileChunked } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Music, Package, Mail, Settings, CheckCircle2, XCircle, Image as ImageIcon, Pencil, Layers, KeyRound, LayoutGrid } from "lucide-react";
import { EditTrackModal, EditPackModal } from "@/components/EditModal";
import { BulkUpload } from "@/components/BulkUpload";
import { SortableTrackList } from "@/components/SortableTrackList";
import { TagsInput, tagsToForm } from "@/components/TagsInput";
import { PriceToggle } from "@/components/PriceToggle";
import { DataTransfer } from "@/components/DataTransfer";
import { HighlightSelect, sectionsToForm } from "@/components/HighlightSelect";
import { splitGenres } from "@/components/GenrePill";
import { AccessCodesAdmin } from "@/components/AccessCodesAdmin";
import { SectionsAdmin } from "@/components/SectionsAdmin";

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("track");
  const [tracks, setTracks] = useState([]);
  const [packs, setPacks] = useState([]);
  const [requests, setRequests] = useState([]);
  const [config, setConfig] = useState({});
  const [orders, setOrders] = useState([]);

  const refresh = async () => {
    if (!user?.is_admin) return;
    const [t, p, r, c, o] = await Promise.all([
      api.get("/tracks?limit=5000"),
      api.get("/packs"),
      api.get("/admin/remix-requests"),
      api.get("/config"),
      api.get("/admin/bizum-orders"),
    ]);
    setTracks(t.data); setPacks(p.data); setRequests(r.data); setConfig(c.data); setOrders(o.data);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [user]);

  if (loading) return <Center>Verificando sesión...</Center>;
  if (!user) return (
    <Center>
      <div className="text-center">
        <h1 className="font-display font-black text-3xl mb-4">Acceso admin</h1>
        <p className="text-white/50 mb-6">Inicia sesión con Google para gestionar tu música.</p>
        <button data-testid="admin-page-login"
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + "/admin";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="btn-red text-white px-6 py-3 rounded-full font-semibold">
          Login con Google
        </button>
      </div>
    </Center>
  );
  if (!user.is_admin) return (
    <Center>
      <div className="text-center">
        <h1 className="font-display font-black text-3xl mb-2 text-red-500">Sin permisos</h1>
        <p className="text-white/50">Solo el admin puede acceder aquí.</p>
      </div>
    </Center>
  );

  const pendingCount = orders.filter((o) => o.status === "pending").length;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-mono text-xs text-red-500 uppercase tracking-[0.3em] mb-1">Panel POMATA</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight text-white">
            Hola, {user.name?.split(" ")[0]}
          </h1>
        </div>
      </div>

      <div className="flex gap-1 mb-6 border-b border-white/5 overflow-x-auto">
        <Tab active={tab === "track"} onClick={() => setTab("track")} icon={Music} label="Tracks" testid="admin-tab-track" />
        <Tab active={tab === "bulk"} onClick={() => setTab("bulk")} icon={Layers} label="Subida múltiple" testid="admin-tab-bulk" />
        <Tab active={tab === "pack"} onClick={() => setTab("pack")} icon={Package} label="Packs" testid="admin-tab-pack" />
        <Tab active={tab === "sections"} onClick={() => setTab("sections")} icon={LayoutGrid} label="Secciones" testid="admin-tab-sections" />
        <Tab active={tab === "orders"} onClick={() => setTab("orders")} icon={CheckCircle2} label={`Pagos ${pendingCount > 0 ? `(${pendingCount})` : ""}`} testid="admin-tab-orders" />
        <Tab active={tab === "codes"} onClick={() => setTab("codes")} icon={KeyRound} label="Códigos" testid="admin-tab-codes" />
        <Tab active={tab === "requests"} onClick={() => setTab("requests")} icon={Mail} label="Solicitudes" testid="admin-tab-requests" />
        <Tab active={tab === "config"} onClick={() => setTab("config")} icon={Settings} label="Config" testid="admin-tab-config" />
      </div>

      {tab === "track" && <TrackAdmin tracks={tracks} onChange={refresh} />}
      {tab === "bulk" && <BulkUpload onDone={refresh} />}
      {tab === "pack" && <PackAdmin packs={packs} onChange={refresh} />}
      {tab === "sections" && <SectionsAdmin onChange={refresh} />}
      {tab === "orders" && <OrdersTab orders={orders} onChange={refresh} />}
      {tab === "codes" && <AccessCodesAdmin tracks={tracks} packs={packs} />}
      {tab === "requests" && <Requests requests={requests} />}
      {tab === "config" && <ConfigTab config={config} onChange={refresh} />}
    </div>
  );
}

function Tab({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold transition-colors whitespace-nowrap ${
        active ? "text-white border-b-2 border-red-600" : "text-white/40 hover:text-white/80"
      }`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function TrackAdmin({ tracks, onChange }) {
  const [f, setF] = useState({ title: "", artist: "", category: "remix", genre: "", bpm: "", tags: ["", "", ""], price: "0.99", is_featured: false, is_top: false, is_highlighted: false, is_new: false, highlight_genres: [], sections: [], preview_start: 0 });
  const [audio, setAudio] = useState(null);
  const [cover, setCover] = useState(null);
  const [versions, setVersions] = useState([]);
  const [versionMeta, setVersionMeta] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [editing, setEditing] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!audio || !f.title || !f.artist) return toast.error("Faltan campos requeridos");
    setUploading(true);
    setProgress(0);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, k === "tags" ? tagsToForm(v) : (k === "sections" || k === "highlight_genres") ? sectionsToForm(v) : v));
    fd.append("audio", audio);
    if (cover) fd.append("cover", cover);
    versions.forEach((v) => fd.append("versions", v));
    if (versions.length) fd.append("version_meta", JSON.stringify(versionMeta));
    try {
      await api.post("/admin/tracks", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => ev.total && setProgress(Math.round((ev.loaded / ev.total) * 100)),
      });
      toast.success("Track subido");
      setF({ title: "", artist: "", category: "remix", genre: "", bpm: "", tags: ["", "", ""], price: "0.99", is_featured: false, is_top: false, is_highlighted: false, is_new: false, highlight_genres: [], sections: [], preview_start: 0 });
      setAudio(null); setCover(null);
      onChange();
    } catch (err) {
      toast.error("Error al subir");
      console.error(err);
    } finally { setUploading(false); }
  };

  const del = async (id) => {
    if (!confirm("¿Eliminar este track?")) return;
    await api.delete(`/admin/tracks/${id}`);
    toast.success("Eliminado");
    onChange();
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <form onSubmit={submit} className="lg:col-span-2 bg-[#111116] border border-white/5 rounded-2xl p-5 space-y-3">
        <h3 className="font-display font-bold text-lg mb-2">Subir Track</h3>
        <TInput placeholder="Título" value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="admin-track-title" />
        <TInput placeholder="Artista" value={f.artist} onChange={(v) => setF({ ...f, artist: v })} testid="admin-track-artist" />
        <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}
          data-testid="admin-track-category"
          className="w-full bg-[#16161D] border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
          <option value="remix">Remix</option>
          <option value="mashup">Mashup</option>
          <option value="original">Original</option>
          <option value="pool">Pool</option>
        </select>
        <TInput placeholder="Género(s), separados por coma" value={f.genre} onChange={(v) => setF({ ...f, genre: v })} testid="admin-track-genre" />
        <TInput placeholder="BPM" value={f.bpm} onChange={(v) => setF({ ...f, bpm: v })} type="number" testid="admin-track-bpm" />
        <div>
          <span className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1 block">Precio</span>
          <PriceToggle value={f.price} onChange={(price) => setF({ ...f, price })} testid="admin-track-price" />
        </div>
        <TagsInput value={f.tags} onChange={(tags) => setF({ ...f, tags })} testid="admin-track-tag" />
        <HighlightSelect value={{ is_featured: f.is_featured, is_top: f.is_top, is_highlighted: f.is_highlighted, is_new: f.is_new, highlight_genres: f.highlight_genres, sections: f.sections }} genres={splitGenres(f.genre)} category={f.category} onChange={(v) => setF({ ...f, ...v })} testid="admin-track-highlight" />
        <FileField label="Audio (MP3/WAV)" file={audio} accept="audio/*" testid="admin-track-audio"
          onChange={(file) => { setAudio(file); if (file && !f.title) { const base = file.name.replace(/\.[^.]+$/, ""); const m = /^(.+?)\s+[-–]\s+(.+)$/.exec(base); setF((x) => ({ ...x, title: m ? m[2].trim() : base, artist: m ? m[1].trim() : x.artist })); } }} />
        <MultiFileDrop label="Versiones extra (Extended, Radio Edit…) — opcional" testid="admin-track-versions"
          onFiles={(files) => { setVersions(files); setVersionMeta(files.map((file) => { const base = file.name.replace(/\.[^.]+$/, ""); const m = /^(.+?)\s+[-–]\s+(.+)$/.exec(base); return { title: m ? m[2].trim() : base, artist: m ? m[1].trim() : f.artist }; })); }} />
        {versions.map((file, i) => (
          <div key={file.name + i} className="grid grid-cols-[1fr_120px] gap-2 pl-2 border-l-2 border-red-900/50" data-testid={`admin-version-meta-${i}`}>
            <input className="w-full bg-[#16161D] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs" placeholder="Título de la versión (Extended Mix…)"
              value={versionMeta[i]?.title || ""} data-testid={`admin-version-title-${i}`}
              onChange={(e) => setVersionMeta((m) => m.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
            <input className="w-full bg-[#16161D] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs" placeholder="Artista"
              value={versionMeta[i]?.artist || ""} data-testid={`admin-version-artist-${i}`}
              onChange={(e) => setVersionMeta((m) => m.map((x, j) => (j === i ? { ...x, artist: e.target.value } : x)))} />
            <span className="col-span-2 text-[10px] text-white/30 truncate">{file.name} (se descargará con este nombre)</span>
          </div>
        ))}
        <FileField label="Portada (opcional)" file={cover} onChange={setCover} accept="image/*" testid="admin-track-cover" />
        {uploading && (
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-red-600 transition-[width]" style={{ width: `${progress}%` }} />
          </div>
        )}
        <button type="submit" disabled={uploading} data-testid="admin-track-submit"
          className="w-full btn-red text-white py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
          <Upload className="w-4 h-4" /> {uploading ? `Subiendo ${progress}%` : "Subir Track"}
        </button>
      </form>

      {editing && <EditTrackModal track={editing} onClose={() => setEditing(null)} onSaved={onChange} />}

      <div className="lg:col-span-3 space-y-2">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display font-bold text-lg">Tracks</h3>
          <span className="text-[11px] text-white/40 font-mono uppercase tracking-widest">Arrastra ⋮ para ordenar</span>
        </div>
        <SortableTrackList tracks={tracks} onEdit={setEditing} onDelete={del} onReordered={onChange} />
      </div>
    </div>
  );
}

function PackAdmin({ packs, onChange }) {
  const [f, setF] = useState({ title: "", description: "", price: "24.99", is_featured: false, is_new: false, coming_soon: false });
  const [cover, setCover] = useState(null);
  const [mp3s, setMp3s] = useState([]);
  const [pistaVersions, setPistaVersions] = useState({});
  const [zip, setZip] = useState(null);
  const [zipProgress, setZipProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [editing, setEditing] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title) return toast.error("El título es obligatorio");
    if (!mp3s.length) return toast.error("Sube al menos una pista MP3");
    setUploading(true);
    setProgress(0);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    if (cover) fd.append("cover", cover);
    mp3s.forEach((m) => fd.append("tracks", m));
    const vmap = [];
    mp3s.forEach((_, i) => (pistaVersions[i] || []).forEach((vf) => { fd.append("version_files", vf); vmap.push(i); }));
    if (vmap.length) fd.append("versions_map", JSON.stringify(vmap));
    try {
      if (zip) { setZipProgress(0); const path = await uploadFileChunked(zip, { folder: "packs", contentType: "application/zip", onProgress: setZipProgress }); fd.append("archive_path", path); }
      await api.post("/admin/packs", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => ev.total && setProgress(Math.round((ev.loaded / ev.total) * 100)),
      });
      toast.success("Pack subido");
      setF({ title: "", description: "", price: "24.99", is_featured: false, is_new: false, coming_soon: false });
      setCover(null); setMp3s([]); setPistaVersions({}); setZip(null); setZipProgress(0);
      onChange();
    } catch {
      toast.error("Error al subir");
    } finally { setUploading(false); }
  };

  const del = async (id) => {
    if (!confirm("¿Eliminar pack?")) return;
    await api.delete(`/admin/packs/${id}`);
    onChange();
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <form onSubmit={submit} className="lg:col-span-2 bg-[#111116] border border-white/5 rounded-2xl p-5 space-y-3">
        <h3 className="font-display font-bold text-lg mb-2">Subir Pack</h3>
        <TInput placeholder="Título" value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="admin-pack-title" />
        <textarea placeholder="Descripción" value={f.description}
          onChange={(e) => setF({ ...f, description: e.target.value })} rows={3}
          className="w-full bg-[#16161D] border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
        <div>
          <span className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1 block">Precio</span>
          <PriceToggle value={f.price} onChange={(price) => setF({ ...f, price })} testid="admin-pack-price" defaultPaid="24.99" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="flex items-center gap-2 text-xs text-white/70 rounded-lg border border-white/10 px-3 py-2 cursor-pointer">
            <input type="checkbox" checked={f.is_new} onChange={(e) => setF({ ...f, is_new: e.target.checked })} data-testid="admin-pack-new" className="accent-red-600" /> Novedad (Inicio)
          </label>
          <label className="flex items-center gap-2 text-xs text-white/70 rounded-lg border border-white/10 px-3 py-2 cursor-pointer" title="El pack completo no se puede comprar ni descargar; solo las pistas sueltas">
            <input type="checkbox" checked={f.coming_soon} onChange={(e) => setF({ ...f, coming_soon: e.target.checked })} data-testid="admin-pack-coming-soon" className="accent-red-600" /> Próximamente
          </label>
        </div>
        <MultiFileDrop label="Pistas MP3 (el ZIP del pack se genera automáticamente al descargar)" testid="admin-pack-tracks" onFiles={(files) => { setMp3s(files); setPistaVersions({}); }} count={mp3s.length} />
        <label className="block rounded-xl border border-dashed border-white/15 p-3 cursor-pointer hover:border-red-600/50" data-testid="admin-pack-zip">
          <span className="font-mono text-[10px] uppercase tracking-widest text-white/50 block mb-1">ZIP manual del pack (opcional)</span>
          <span className="text-[11px] text-white/40 block">Si lo subes, se entregará este archivo en vez del ZIP generado automáticamente.</span>
          <input type="file" accept=".zip,application/zip" className="hidden" data-testid="admin-pack-zip-input" onChange={(e) => setZip(e.target.files[0] || null)} />
          {zip && <span className="text-xs text-emerald-400 mt-1 block" data-testid="admin-pack-zip-name">{zip.name} · {(zip.size / 1024 / 1024).toFixed(1)} MB{uploading && zipProgress > 0 ? ` · subiendo ${zipProgress}%` : ""}</span>}
        </label>
        {mp3s.length > 0 && (
          <div className="max-h-56 overflow-y-auto space-y-1 pr-1" data-testid="admin-pack-file-list">
            {mp3s.map((m, i) => (
              <div key={`${m.name}-${i}`} className="flex items-center gap-2 text-[11px] text-white/60 bg-black/30 rounded-lg px-2 py-1" data-testid={`admin-pack-file-${i}`}>
                <span className="truncate flex-1">{m.name}</span>
                {(pistaVersions[i] || []).length > 0 && <span className="text-emerald-400 shrink-0">{pistaVersions[i].length} versión{pistaVersions[i].length === 1 ? "" : "es"}</span>}
                <label className="shrink-0 cursor-pointer text-red-400 hover:text-white border border-dashed border-red-800/50 rounded-full px-2 py-0.5" data-testid={`admin-pack-file-versions-${i}`}>
                  + versiones
                  <input type="file" multiple accept="audio/*" className="hidden" data-testid={`admin-pack-file-versions-input-${i}`}
                    onChange={(e) => { const files = Array.from(e.target.files); setPistaVersions((pv) => ({ ...pv, [i]: [...(pv[i] || []), ...files] })); e.target.value = ""; }} />
                </label>
              </div>
            ))}
          </div>
        )}
        <FileField label="Portada" file={cover} onChange={setCover} accept="image/*" testid="admin-pack-cover" />
        {uploading && (
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-red-600 transition-[width]" style={{ width: `${progress}%` }} />
          </div>
        )}
        <button type="submit" disabled={uploading} data-testid="admin-pack-submit"
          className="w-full btn-red text-white py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
          <Upload className="w-4 h-4" /> {uploading ? `Subiendo ${progress}%` : "Subir Pack"}
        </button>
      </form>

      {editing && <EditPackModal pack={editing} onClose={() => setEditing(null)} onSaved={onChange} />}

      <div className="lg:col-span-3 space-y-2">
        <h3 className="font-display font-bold text-lg mb-2">Packs ({packs.length})</h3>
        {packs.map((p) => (
          <div key={p.id} className="bg-[#111116] border border-white/5 rounded-lg p-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded overflow-hidden bg-neutral-900 shrink-0" data-testid={`admin-pack-cover-${p.id}`}>
                {p.cover_path ? <img src={coverUrl(p.cover_path)} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4 text-red-500 m-auto mt-4" />}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{p.title}</div>
                <div className="text-xs text-white/40">{p.tracks?.length || 0} pistas · {p.price === 0 ? "Free" : `€${p.price.toFixed(2)}`}</div>
              </div>
            </div>
            <div className="flex items-center shrink-0">
              <button onClick={() => setEditing(p)} data-testid={`admin-edit-pack-${p.id}`}
                className="text-white/60 hover:text-white hover:bg-white/5 p-2 rounded-lg"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(p.id)} data-testid={`admin-delete-pack-${p.id}`} className="text-red-500 hover:bg-red-600/10 p-2 rounded-lg"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrdersTab({ orders, onChange }) {
  const checkPaypal = async (id) => {
    try {
      const r = await api.post(`/admin/paypal/orders/${id}/check`);
      if (r.data.status === "paid") toast.success(r.data.captured_now ? "Pago capturado ahora en PayPal ✔" : "Pago confirmado en PayPal ✔");
      else toast(r.data.message || `Estado PayPal: ${r.data.paypal_status}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? d?.message : d || "No se pudo comprobar");
    }
    onChange();
  };

  const markPaid = async (id) => {
    if (!confirm("¿Confirmar que has recibido el pago?")) return;
    await api.post(`/admin/bizum-orders/${id}/mark-paid`);
    toast.success("Pago confirmado. El cliente puede descargar.");
    onChange();
  };
  const cancel = async (id) => {
    if (!confirm("¿Cancelar este pedido?")) return;
    await api.post(`/admin/bizum-orders/${id}/cancel`);
    onChange();
  };

  return (
    <div className="space-y-3">
      <div className="bg-red-950/20 border border-red-900/40 rounded-xl p-4 text-sm text-white/70">
        <b className="text-red-400">PayPal Checkout:</b> los pagos se verifican automáticamente en el servidor y el cliente descarga al instante. Aquí ves el historial de compras (pagadas, creadas sin completar o fallidas). Después haz clic en "Confirmar pago" — el cliente recibirá el enlace de descarga automáticamente.
      </div>
      {orders.length === 0 && <div className="text-white/40 py-10 text-center">Sin pedidos aún.</div>}
      {orders.map((o) => (
        <div key={o.id} data-testid={`bizum-order-${o.id}`} className="bg-[#111116] border border-white/5 rounded-xl p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-xs uppercase tracking-widest text-red-400 bg-red-950/40 border border-red-800/50 px-2 py-0.5 rounded">
                  {o.code}
                </span>
                <StatusBadge status={o.status} />
                <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${o.payment_method === "paypal" ? "text-sky-400 border-sky-800/50 bg-sky-950/40" : "text-white/60 border-white/10"}`} data-testid={`order-method-${o.id}`}>{o.payment_method === "paypal" ? "PayPal" : "Bizum"}</span>
              </div>
              <div className="font-bold">{o.item_title}</div>
              <div className="text-xs text-white/40">{o.buyer_name} · <a href={`mailto:${o.buyer_email}`} className="text-red-500 hover:underline">{o.buyer_email}</a></div>
              <div className="text-xs text-white/40 mt-1">{new Date(o.created_at).toLocaleString()}</div>
              {o.payment_method === "paypal" && o.paypal_order_id && <div className="text-[10px] font-mono text-white/30 mt-1" data-testid={`order-paypal-id-${o.id}`}>PayPal ID: {o.paypal_order_id}{o.paypal_capture_id ? ` · captura ${o.paypal_capture_id}` : ""}</div>}
              {o.paypal_error && o.status !== "paid" && <div className="text-xs text-amber-400 mt-1" data-testid={`order-paypal-error-${o.id}`}>{o.paypal_error}</div>}
            </div>
            <div className="text-right">
              <div className="font-display font-black text-2xl text-white mb-2">€{o.amount.toFixed(2)}</div>
              {o.payment_method === "paypal" && o.status !== "paid" && o.paypal_order_id && (
                <button onClick={() => checkPaypal(o.id)} data-testid={`check-paypal-${o.id}`}
                  className="text-sky-400 hover:text-white text-xs px-3 py-1.5 rounded-full border border-sky-800/50 hover:bg-sky-900/40 flex items-center gap-1 mb-2 ml-auto">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Comprobar en PayPal
                </button>
              )}
              {o.status === "pending" && (
                <div className="flex gap-2">
                  <button onClick={() => markPaid(o.id)} data-testid={`mark-paid-${o.id}`}
                    className="btn-red text-white text-xs px-3 py-1.5 rounded-full font-semibold flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Confirmar pago
                  </button>
                  <button onClick={() => cancel(o.id)} data-testid={`cancel-order-${o.id}`}
                    className="text-white/60 hover:text-red-500 text-xs px-3 py-1.5 rounded-full border border-white/10 flex items-center gap-1">
                    <XCircle className="w-3.5 h-3.5" /> Cancelar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: "text-amber-400 border-amber-800/50 bg-amber-950/40",
    paid: "text-emerald-400 border-emerald-800/50 bg-emerald-950/40",
    canceled: "text-white/40 border-white/10 bg-white/5",
    created: "text-white/40 border-white/10 bg-white/5",
    failed: "text-red-400 border-red-800/50 bg-red-950/40",
  };
  const label = { pending: "Pendiente", paid: "Pagado", canceled: "Cancelado", created: "Sin completar", failed: "Fallido" }[status] || status;
  return <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${map[status]}`}>{label}</span>;
}

function Requests({ requests }) {
  return (
    <div className="space-y-3">
      {requests.length === 0 && <div className="text-white/40 py-10 text-center">Sin solicitudes aún.</div>}
      {requests.map((r) => (
        <div key={r.id} className="bg-[#111116] border border-white/5 rounded-xl p-5">
          <div className="flex justify-between items-start gap-3 mb-2">
            <div>
              <div className="font-bold">{r.name}</div>
              <a href={`mailto:${r.email}`} className="text-red-500 text-sm hover:underline">{r.email}</a>
            </div>
            <div className="font-mono text-xs text-white/40">{new Date(r.created_at).toLocaleDateString()}</div>
          </div>
          {r.song_title && <div className="text-sm text-white/70"><b>Canción:</b> {r.song_title}</div>}
          {r.target_genre && <div className="text-sm text-white/70"><b>Estilo:</b> {r.target_genre}</div>}
          {r.notes && <div className="text-sm text-white/70 mt-2 whitespace-pre-wrap">{r.notes}</div>}
        </div>
      ))}
    </div>
  );
}

function ConfigTab({ config, onChange }) {
  const [c, setC] = useState(config);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  useEffect(() => setC(config), [config]);

  const save = async () => {
    await api.put("/admin/config", c);
    toast.success("Config actualizada");
    onChange();
  };

  const uploadImage = async (file, kind, key) => {
    if (!file) return;
    const setter = kind === "logo" ? setUploadingLogo : setUploadingBanner;
    setter(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newC = { ...c, [key]: res.data.path };
      setC(newC);
      await api.put("/admin/config", newC);
      toast.success(`${kind === "logo" ? "Logo" : "Banner"} actualizado`);
      onChange();
    } catch {
      toast.error("Error al subir imagen");
    } finally { setter(false); }
  };

  const fields = [
    ["artist_name", "Nombre de artista"],
    ["tagline", "Tagline / lema"],
    ["contact_email", "Email de contacto"],
    ["instagram", "Instagram URL"],
    ["youtube", "YouTube URL"],
    ["soundcloud", "SoundCloud URL"],
    ["spotify", "Spotify URL"],
    ["tiktok", "TikTok URL"],
  ];

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-[#111116] border border-white/5 rounded-2xl p-6">
          <h3 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><ImageIcon className="w-5 h-5 text-red-500" /> Logotipo</h3>
          {c.logo_path && (
            <div className="mb-3 p-3 bg-black/50 rounded-lg border border-white/5 h-24 flex items-center justify-center">
              <img src={coverUrl(c.logo_path, 0)} alt="logo" className="max-h-16 max-w-full object-contain" />
            </div>
          )}
          <input type="file" accept="image/*" data-testid="admin-logo-upload"
            onChange={(e) => uploadImage(e.target.files[0], "logo", "logo_path")}
            disabled={uploadingLogo}
            className="w-full text-xs text-white/60 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-red-600/20 file:text-red-400 file:font-semibold hover:file:bg-red-600/30" />
          {uploadingLogo && <div className="text-xs text-white/50 mt-2">Subiendo...</div>}
        </div>

        <div className="bg-[#111116] border border-white/5 rounded-2xl p-6">
          <h3 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><ImageIcon className="w-5 h-5 text-red-500" /> Banner panorámico</h3>
          {c.banner_path && (
            <div className="mb-3 rounded-lg overflow-hidden border border-white/5 aspect-[16/6]">
              <img src={coverUrl(c.banner_path, 0)} alt="banner" className="w-full h-full object-cover" />
            </div>
          )}
          <input type="file" accept="image/*" data-testid="admin-banner-upload"
            onChange={(e) => uploadImage(e.target.files[0], "banner", "banner_path")}
            disabled={uploadingBanner}
            className="w-full text-xs text-white/60 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-red-600/20 file:text-red-400 file:font-semibold hover:file:bg-red-600/30" />
          <div className="text-[11px] text-white/40 mt-2">Recomendado: 2000×750px (formato panorámico)</div>
          {uploadingBanner && <div className="text-xs text-white/50 mt-2">Subiendo...</div>}
        </div>
      </div>

      <DataTransfer onDone={onChange} />

      <div className="max-w-2xl bg-[#111116] border border-white/5 rounded-2xl p-6 space-y-3">
        <h3 className="font-display font-bold text-lg mb-3">Configuración del sitio</h3>
        <label className="flex items-center gap-2 text-sm text-white/70">
          <input type="checkbox" checked={!!c.show_stats} onChange={(e) => setC({ ...c, show_stats: e.target.checked })} data-testid="config-show-stats" />
          Mostrar contador de reproducciones y descargas al público
        </label>
        {fields.map(([k, label]) => (
          <label key={k} className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1 block">{label}</span>
            <input value={c[k] || ""} onChange={(e) => setC({ ...c, [k]: e.target.value })}
              data-testid={`config-${k}`}
              className="w-full bg-[#16161D] border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
          </label>
        ))}
        <button onClick={save} data-testid="admin-config-save"
          className="btn-red text-white px-6 py-2.5 rounded-lg font-semibold">
          Guardar
        </button>
      </div>
    </div>
  );
}

function TInput({ placeholder, value, onChange, type = "text", testid }) {
  return (
    <input placeholder={placeholder} value={value} type={type} data-testid={testid}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[#16161D] border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
  );
}

function MultiFileDrop({ label, onFiles, testid, count }) {
  const [over, setOver] = useState(false);
  const [n, setN] = useState(0);
  const inputRef = React.useRef(null);
  const shown = count ?? n;
  const take = (list) => { const files = Array.from(list || []).filter((f) => f.type.startsWith("audio/") || /\.(mp3|wav|flac|aiff?|m4a|ogg)$/i.test(f.name)); if (!files.length) return toast.error("Suelta archivos de audio"); setN(files.length); onFiles(files); };
  return (
    <div>
      <span className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1 block">{label}</span>
      <div role="button" tabIndex={0} data-testid={`${testid}-dropzone`}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files); }}
        className={`cursor-pointer rounded-lg border border-dashed px-3 py-3 text-center text-xs transition-colors ${
          over ? "border-red-500 bg-red-950/20 text-white" : shown > 0 ? "border-emerald-700/50 bg-emerald-950/10 text-emerald-400" : "border-white/15 bg-[#16161D] text-white/50 hover:border-white/30"
        }`}>
        <input ref={inputRef} type="file" multiple accept="audio/*" className="hidden" data-testid={testid}
          onChange={(e) => { take(e.target.files); e.target.value = ""; }} />
        {shown > 0 ? `${shown} archivo${shown === 1 ? "" : "s"} seleccionado${shown === 1 ? "" : "s"}` : "Arrastra varios archivos aquí o haz clic para seleccionar"}
      </div>
    </div>
  );
}

function FileField({ label, file, onChange, accept, testid }) {
  const [over, setOver] = useState(false);
  const inputRef = React.useRef(null);
  const matches = (f) => !accept || accept.split(",").some((a) => (a.trim().startsWith(".") ? f.name.toLowerCase().endsWith(a.trim()) : new RegExp("^" + a.trim().replace("*", ".*") + "$").test(f.type)));
  const take = (files) => { const f = Array.from(files || []).find(matches); if (f) onChange(f); else toast.error("Tipo de archivo no válido"); };
  return (
    <div>
      <span className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1 block">{label}</span>
      <div role="button" tabIndex={0} data-testid={`${testid}-dropzone`}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files); }}
        className={`cursor-pointer rounded-lg border border-dashed px-3 py-3 text-center text-xs transition-colors ${
          over ? "border-red-500 bg-red-950/20 text-white" : file ? "border-emerald-700/50 bg-emerald-950/10 text-emerald-400" : "border-white/15 bg-[#16161D] text-white/50 hover:border-white/30"
        }`}>
        <input ref={inputRef} type="file" accept={accept} className="hidden" data-testid={testid}
          onChange={(e) => { if (e.target.files[0]) onChange(e.target.files[0]); e.target.value = ""; }} />
        {file ? <span className="truncate block">{file.name}</span> : <span>Arrastra aquí o haz clic para seleccionar</span>}
      </div>
    </div>
  );
}

function Center({ children }) {
  return <div className="min-h-[60vh] flex items-center justify-center px-5">{children}</div>;
}
